/**
 * Normalize a device serial number to a canonical form (client-side mirror
 * of server/utils/normalizeSerialNumber.js).
 *
 * Transformations applied:
 *  1. Trim leading/trailing whitespace
 *  2. Strip all internal whitespace, non-breaking spaces, and zero-width characters
 *  3. Replace en-dash (–) and em-dash (—) with a regular hyphen (-)
 *  4. Strip smart/curly quotes (copy-paste artefacts)
 *  5. Convert to UPPERCASE (case-insensitive identity)
 *
 * @param {string} serial - The raw serial number
 * @returns {string} The normalized serial number
 */
export function normalizeSerialNumber(serial) {
  if (typeof serial !== 'string') return serial;

  return serial
    .trim()
    .replace(/[\s\u00A0\u200B\u200C\u200D\uFEFF]+/g, '')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019\u201C\u201D]/g, '')
    .toUpperCase();
}

/**
 * Allowed characters in a serial number after normalization.
 * Only uppercase letters, digits, and hyphens are permitted.
 */
const SERIAL_NUMBER_PATTERN = /^[A-Z0-9-]+$/;

/**
 * Validate that a (normalized) serial number contains only allowed characters.
 *
 * @param {string} serial - A serial number that has already been normalized.
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateSerialNumber(serial) {
  if (!serial || typeof serial !== 'string') {
    return { valid: false, error: 'Serial number is required' };
  }
  if (!SERIAL_NUMBER_PATTERN.test(serial)) {
    return { valid: false, error: 'Serial number can only contain letters (A-Z), digits (0-9), and hyphens (-)' };
  }
  return { valid: true };
}
